export class Store<T> {
    private data: Record<string, T> = {};
    private mappers: Record<string, Record<string, (value: T) => T>> = {};
    private callbacks: (() => void)[] = [];
    private cachedSnapshot: Record<string, T> = {};
    private hasChanged = false;
    private updateDelayMs = 10;
    private updateTimeout?: number;

    public subscribe = (callback: () => void): (() => void) => {
        this.callbacks.push(callback);
        return () => {
            this.callbacks.filter((c) => c !== callback);
        };
    };

    public getSnapshot = (): Readonly<Record<string, T>> => {
        if (this.hasChanged) {
            this.cachedSnapshot = { ...this.data };
            this.hasChanged = false;
        }
        return this.cachedSnapshot;
    };

    public get(key: string): T | undefined {
        return this.data[key];
    }

    public set(key: string, value: T): void {
        this.data[key] = value;

        const mappers = this.mappers[key];
        if (mappers) {
            for (const [mapKey, mapper] of Object.entries(mappers)) {
                this.data[mapKey] = mapper(value);
            }
        }

        this.hasChanged = true;
        this.update();
    }

    public setMany(values: Record<string, T>): void {
        for (const [key, value] of Object.entries(values)) {
            this.data[key] = value;
        }
        this.hasChanged = true;
        this.update();
    }

    public change(key: string, change: (value: T) => T): void {
        this.set(key, change(this.data[key]));
    }

    private update(): void {
        if (this.updateDelayMs <= 0) {
            this.callbacks.forEach((callback) => callback());
            return;
        }
        if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
        }
        this.updateTimeout = setTimeout(() => {
            this.updateTimeout = undefined;
            this.callbacks.forEach((callback) => callback());
        }, this.updateDelayMs);
    }

    public addValueMapper(key: string, otherKey: string, mapper: (value: T) => T): void {
        const existing = this.mappers[key] ?? {};
        this.mappers[key] = { ...existing, [otherKey]: mapper };
    }

    public addEnumMapper(
        key: string,
        otherKey: string,
        mapping: Record<string, T>,
        defaultValue?: T,
    ): void {
        this.addValueMapper(key, otherKey, (value) => {
            if (typeof value !== "string") {
                throw new Error("enum mapper only works on strings");
            }
            return mapping[value] ?? defaultValue ?? value;
        });
    }
}
